import React from 'react'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react-lite'
import { Button } from '@gate/gui'
import { Skeleton, Flex } from '@mantine/core'
import useLangPrefix from '@/hooks/useLangPrefix'
import { eventTracker } from '@/utils/collectEvent'
import { useStore } from '@/store/index'
import BaseEmpty from '@/components/BaseEmpty'
import BaseSuspense from '@/components/BaseSuspense'
import {
  PriceNewsStyled,
  PriceNewsSkeletonContainer,
  PriceNewsBoxDiv,
  PriceNewsTitle,
  FundraisingNewsBoxDiv,
} from './style'
import { useUpdateEffect } from 'ahooks'
import { getHotBlog } from 'api/price'
import { useRouter } from 'next/router'
import { HOME_PAGE_URL } from '@/constants/index'
import { useAdapter } from '@/components/Adapter'

const ArticleItem = React.lazy(() => import('@/blocks/Price/PriceNews/Item'))

const PriceNews: React.FC = (): React.ReactElement => {
  const { locale } = useRouter()
  const { priceClientData, setHotBlog, titleTab } = useStore().baseStore
  const { langPrefix } = useLangPrefix()
  const { t } = useTranslation()
  const { size } = useAdapter()

  const { loading, priceNewsListData } = priceClientData

  useUpdateEffect(() => {
    getHotBlog(
      {
        lang: locale ?? 'en',
      },
      {},
      titleTab
    ).then(res => {
      setHotBlog(res)
    })
  }, [titleTab])

  /**
   * @description: 热点分析 | 骨架屏
   * @param {*} React
   * @return {*}
   */
  const PriceNewsItemSkeleton = (): React.ReactElement => {
    return (
      <PriceNewsSkeletonContainer>
        <Skeleton w={'100%'} mb={16} h={216} />
        <Flex justify="center">
          <Skeleton w={'98%'} h={40} />
        </Flex>
      </PriceNewsSkeletonContainer>
    )
  }

  if (titleTab === HOME_PAGE_URL.Fundraising) {
    return (
      <PriceNewsStyled>
        <PriceNewsTitle>{t('p.news', { ns: 'fundraising' })}</PriceNewsTitle>

        <BaseSuspense
          loading={loading}
          fallback={
            <FundraisingNewsBoxDiv>
              {size === 'sm' ? (
                <>
                  <PriceNewsItemSkeleton />
                  <PriceNewsItemSkeleton />
                </>
              ) : (
                <>
                  <PriceNewsItemSkeleton />
                  <PriceNewsItemSkeleton />
                  <PriceNewsItemSkeleton />
                  <PriceNewsItemSkeleton />
                </>
              )}
            </FundraisingNewsBoxDiv>
          }
        >
          {priceNewsListData?.length ? (
            <FundraisingNewsBoxDiv>
              {priceNewsListData?.map(item => <ArticleItem isFundraising key={item.title} {...item} />)}
            </FundraisingNewsBoxDiv>
          ) : (
            <Flex py={48}>
              <BaseEmpty />
            </Flex>
          )}
        </BaseSuspense>
      </PriceNewsStyled>
    )
  }

  return (
    <PriceNewsStyled>
      <Flex align="center" justify="space-between">
        <PriceNewsTitle>{t('P.Highlights')}</PriceNewsTitle>
        <a
          onClick={() => {
            eventTracker('price_sectors_click', {
              button_name: 'highlights_more',
            })
          }}
          href={`${langPrefix}/blog`}
          target="_blank"
        >
          <Button size="xs" type="outlined" label={t('P.More') as string} className="text-1 size-12 font-500" />
        </a>
      </Flex>

      <BaseSuspense
        loading={loading}
        fallback={
          <PriceNewsBoxDiv>
            <PriceNewsItemSkeleton />
            <PriceNewsItemSkeleton />
            <PriceNewsItemSkeleton />
          </PriceNewsBoxDiv>
        }
      >
        {priceNewsListData?.length ? (
          <PriceNewsBoxDiv>
            {priceNewsListData?.map((item, index) => <ArticleItem key={'id' + item.uri + index} {...item} />)}
          </PriceNewsBoxDiv>
        ) : (
          <Flex py={48}>
            <BaseEmpty />
          </Flex>
        )}
      </BaseSuspense>
    </PriceNewsStyled>
  )
}

export default observer(PriceNews)
